import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import styles from "./Wheel.module.css";
import { useTranslation } from "react-i18next";

export type WheelProps = {
  /** Labels for each segment */
  segments: string[];
  /** Optional array of CSS color strings (will auto‑generate if not supplied) */
  colors?: string[];
  /** Diameter in pixels */
  size?: number;
  /** Called with the winning segment when the wheel stops */
  onFinished?: (winner: string) => void;
};

const Wheel: React.FC<WheelProps> = ({
  segments,
  colors,
  size = 300,
  onFinished,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const rafRef = useRef<number | null>(null);

  const segmentCount = segments.length;

  const segmentColors = useMemo(() => {
    if (colors && colors.length >= segmentCount) return colors;
    return segments.map((_, i) => `hsl(${(i * 360) / segmentCount}, 70%, 50%)`);
  }, [colors, segments, segmentCount]);

  const draw = useCallback(
    (rot: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const half = size / 2;
      const arc = (2 * Math.PI) / segmentCount;

      ctx.clearRect(0, 0, size, size);
      ctx.save();
      ctx.translate(half, half);
      ctx.rotate(rot);

      segments.forEach((text, i) => {
        const start = i * arc;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, half, start, start + arc);
        ctx.fillStyle = segmentColors[i];
        ctx.fill();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.save();
        ctx.fillStyle = "#000";
        ctx.translate(
          Math.cos(start + arc / 2) * (half - 30),
          Math.sin(start + arc / 2) * (half - 30)
        );
        ctx.rotate(start + arc / 2 + Math.PI / 2);
        ctx.textAlign = "center";
        ctx.font = "bold 14px sans-serif";
        ctx.fillText(text, 0, 0);
        ctx.restore();
      });

      ctx.restore();
    },
    [size, segmentCount, segmentColors, segments]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = size;
      canvas.height = size;
    }
    draw(rotation);
  }, [rotation, size, draw]);

  const spin = () => {
    if (isSpinning) return;
    setIsSpinning(true);

    const totalSpins = Math.random() * 3 + 4;
    const extra = Math.random() * 2 * Math.PI;
    const finalRot = rotation + totalSpins * 2 * Math.PI + extra;
    const duration = 5000 + Math.random() * 2000;
    const start = performance.now();

    const animate = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = rotation + (finalRot - rotation) * eased;
      setRotation(current);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setIsSpinning(false);
        const deg = (current * 180) / Math.PI + 90;
        const idx =
          segmentCount - Math.floor((deg % 360) / (360 / segmentCount)) - 1;
        const winner = segments[(idx + segmentCount) % segmentCount];
        onFinished?.(winner);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
  };

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  return (
    <div
      className={styles.wheelContainer}
      style={{ width: size, height: size + 60 }}
    >
      <canvas ref={canvasRef} className={styles.wheelCanvas} />
      <div className={styles.wheelPointer} />
      <button
        className={styles.wheelButton}
        onClick={spin}
        disabled={isSpinning}
      >
        {isSpinning ? t("wheel_page.spinning") : t("wheel_page.spin")}
      </button>
    </div>
  );
};

export default Wheel;
